// Shared formatting helpers for the MUI customer pages.
import { brainTokens } from "../design/brainTheme";

const c = brainTokens.color;

/** Normalise a timestamp (seconds or ms) to ms. */
export function toMs(ts?: number | null): number | null {
  if (!ts) return null;
  return ts < 1e12 ? ts * 1000 : ts;
}

export function ageDays(ts?: number | null): number | null {
  const ms = toMs(ts);
  if (ms == null) return null;
  return Math.floor((Date.now() - ms) / 86400000);
}

export function relDate(ts?: number | null): string {
  const d = ageDays(ts);
  if (d == null) return "—";
  if (d <= 0) return "today";
  if (d === 1) return "yesterday";
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

/** Last-activity ink: quiet by default, amber when aging, red when stale. */
export function activityColor(ts?: number | null): string {
  const d = ageDays(ts);
  if (d == null) return c.muted;
  if (d > 90) return c.dangerText;
  if (d > 30) return c.warnText2;
  return c.muted;
}

export function fmtUSD(n: number): string {
  if (!isFinite(n)) return "—";
  return `$${Math.round(n).toLocaleString("en-US")}`;
}

/** NZD amounts (same $ presentation as USD; currency is contextual). */
export function fmtNZD(n: number): string {
  if (!isFinite(n)) return "—";
  return `$${Math.round(n).toLocaleString("en-US")}`;
}

export function fmtNZDSigned(n: number): string {
  if (!isFinite(n)) return "—";
  return `${n >= 0 ? "+" : "−"}$${Math.round(Math.abs(n)).toLocaleString("en-US")}`;
}

export function fmtBytes(n?: number): string {
  if (n == null || !isFinite(n)) return "—";
  const gb = n / 1e9;
  if (gb >= 1) return `${gb.toFixed(1)} GB`;
  const mb = n / 1e6;
  return `${Math.round(mb)} MB`;
}

/** dd/mm/yyyy from ms/seconds. */
export function fmtDate(ts?: number | null): string {
  const ms = toMs(ts);
  if (ms == null) return "—";
  const d = new Date(ms);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

export interface SeriesPoint {
  label: string;
  value: number;
}

/**
 * Cumulative knowledge-atom count per month over the trailing 12 months, from a
 * list of thoughts. Mirrors the old GrowthSpark's growthSeries.
 */
export function growthSeries(activity: { created_at?: number | null }[]): SeriesPoint[] {
  const now = new Date();
  const months: { key: string; label: string }[] = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      key: `${d.getFullYear()}-${d.getMonth()}`,
      label: d.toLocaleString("en-US", { month: "short" }),
    });
  }
  const perMonth = new Map<string, number>();
  for (const t of activity) {
    const ms = toMs(t.created_at);
    if (ms == null) continue;
    const d = new Date(ms);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    perMonth.set(key, (perMonth.get(key) ?? 0) + 1);
  }
  // Atoms created before the window still count toward the running total.
  const windowStart = new Date(now.getFullYear(), now.getMonth() - 11, 1).getTime();
  let running = activity.filter((t) => {
    const ms = toMs(t.created_at);
    return ms != null && ms < windowStart;
  }).length;
  return months.map((m) => {
    running += perMonth.get(m.key) ?? 0;
    return { label: m.label, value: running };
  });
}
